import { latFromMercY, lngFromMercX, mercX, mercY } from "./mercator";

type Point = { latitude: number; longitude: number };
type Viewport = { width: number; height: number };

const MAX_STATIC_SIZE = 640;
const MIN_ZOOM = 4;
const MAX_ZOOM = 15;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

/** Match StaticMapImage's proportional 640px cap and object-fill scaling. */
export function fitConcertNearbyMap(
  viewport: Viewport,
  venue: Point,
  branches: Point[],
  selectedBranch: Point | null,
) {
  const { width, height } = viewport;
  const targets = selectedBranch
    ? [venue, selectedBranch]
    : [venue, ...branches];
  const xs = targets.map((point) => mercX(point.longitude));
  const ys = targets.map((point) => mercY(point.latitude));
  const centerX = (Math.min(...xs) + Math.max(...xs)) / 2;
  const centerY = (Math.min(...ys) + Math.max(...ys)) / 2;

  const imageScale = Math.min(1, MAX_STATIC_SIZE / Math.max(width, height));
  const imageWidth = Math.max(1, Math.round(width * imageScale));
  const imageHeight = Math.max(1, Math.round(height * imageScale));
  const scaleX = width / imageWidth;
  const scaleY = height / imageHeight;
  const maxDeltaX = Math.max(...xs.map((x) => Math.abs(x - centerX)), 1e-12);
  const maxDeltaY = Math.max(...ys.map((y) => Math.abs(y - centerY)), 1e-12);
  // Leave room for full pin bodies, the top legend, and the bottom hint.
  const halfWidth = Math.max(24, (width - 96) / 2);
  const halfHeight = Math.max(24, (height - 152) / 2);
  const fitZoom = Math.floor(
    Math.min(
      Math.log2(halfWidth / (256 * maxDeltaX * scaleX)),
      Math.log2(halfHeight / (256 * maxDeltaY * scaleY)),
    ),
  );
  const zoom =
    targets.length === 1 ? MAX_ZOOM : clamp(fitZoom, MIN_ZOOM, MAX_ZOOM);
  const world = 256 * 2 ** zoom;

  return {
    center: { lat: latFromMercY(centerY), lng: lngFromMercX(centerX) },
    zoom,
    width,
    height,
    project(point: Point) {
      return {
        x: width / 2 + (mercX(point.longitude) - centerX) * world * scaleX,
        y: height / 2 + (mercY(point.latitude) - centerY) * world * scaleY,
      };
    },
  };
}

/** Keep the selected-branch card inside the map, even next to an edge. */
export function concertBranchPopupPosition(
  viewport: Viewport,
  pin: { x: number; y: number },
  popupHeight = 96,
) {
  const width = Math.min(240, Math.max(0, viewport.width - 24));
  const topInset = 58;
  const bottomInset = 54;
  const above = pin.y - 44 - 12 - popupHeight;
  const below = pin.y + 12;
  const bottomLimit = Math.max(
    topInset,
    viewport.height - bottomInset - popupHeight,
  );
  const top =
    above >= topInset
      ? above
      : below <= bottomLimit
      ? below
      : pin.y - 44 - topInset >= viewport.height - bottomInset - pin.y
      ? topInset
      : bottomLimit;

  return {
    width,
    left: clamp(
      pin.x - width / 2,
      12,
      Math.max(12, viewport.width - width - 12),
    ),
    top,
  };
}
